import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/utils/api';
import PostBodyRenderer from '@/components/Post/PostBodyRenderer';
import { getMediaUrl } from '@/constants/media';
import { formatRelativeTime } from '@/utils/formatTime';
import { profileLink } from '@/utils/profileLink';
import './HashtagFeed.scss';

interface PostAuthor {
  userId: string;
  username: string;
  displayName?: string;
  avatar?: string;
}

interface PostStats {
  likes: number;
  dislikes: number;
  comments: number;
}

interface HashtagPost {
  id: string;
  body: string;
  mediaUrl?: string;
  createdAt: string;
  postedBy: PostAuthor;
  stats: PostStats;
}

interface HashtagFeedResponse {
  hashtag: string;
  total: number;
  limit: number;
  offset: number;
  posts: HashtagPost[];
}

const LIMIT = 20;

const HashtagFeedPage: React.FC = () => {
  const { tag } = useParams<{ tag: string }>();
  const navigate = useNavigate();

  const [posts, setPosts] = useState<HashtagPost[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const fetchPosts = useCallback(async (nextOffset: number, reset = false) => {
    if (!tag) return;
    setLoading(true);
    setError(null);
    try {
      const { data }: { data: HashtagFeedResponse } = await api.get(
        `/hashtags/${encodeURIComponent(tag)}/posts`,
        { params: { limit: LIMIT, offset: nextOffset } },
      );
      setPosts(prev => reset ? data.posts : [...prev, ...data.posts]);
      setTotal(data.total);
      setHasMore(nextOffset + data.posts.length < data.total);
      setOffset(nextOffset + data.posts.length);
    } catch {
      setError('Failed to load posts for this hashtag.');
    } finally {
      setLoading(false);
    }
  }, [tag]);

  useEffect(() => {
    setPosts([]);
    setOffset(0);
    setHasMore(true);
    fetchPosts(0, true);
  }, [tag, fetchPosts]);

  return (
    <div className="hashtag-feed-page">
      <div className="hashtag-feed-container">
        <header className="hashtag-feed-header">
          <button className="back-btn" onClick={() => navigate(-1)}>← Back</button>
          <div className="hashtag-title">
            <span className="hashtag-symbol">#</span>
            <h1>{tag}</h1>
          </div>
          {total > 0 && (
            <p className="hashtag-count">{total.toLocaleString()} {total === 1 ? 'post' : 'posts'}</p>
          )}
        </header>

        {error && <div className="hashtag-error">{error}</div>}

        <div className="hashtag-posts">
          {posts.map(post => (
            <div key={post.id} className="hashtag-post-card">
              <div className="post-author-row">
                <div
                  className="post-avatar"
                  onClick={() => navigate(profileLink(post.postedBy.username, 'hashtag', { ref: post.id }))}
                  style={{ cursor: 'pointer' }}
                >
                  {post.postedBy.avatar ? (
                    <img src={getMediaUrl(post.postedBy.avatar) || undefined} alt={post.postedBy.username} />
                  ) : (
                    <div className="avatar-placeholder">
                      {(post.postedBy.displayName ?? post.postedBy.username)[0].toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="post-meta">
                  <span
                    className="post-display-name"
                    onClick={() => navigate(profileLink(post.postedBy.username, 'hashtag', { ref: post.id }))}
                  >
                    {post.postedBy.displayName ?? post.postedBy.username}
                  </span>
                  <span className="post-time">{formatRelativeTime(post.createdAt)}</span>
                </div>
              </div>

              <PostBodyRenderer body={post.body} />

              {post.mediaUrl && (
                <div className="post-media">
                  <img src={getMediaUrl(post.mediaUrl) || undefined} alt="Post media" />
                </div>
              )}

              <div className="post-stats-row">
                <span>👍 {post.stats.likes}</span>
                <span>👎 {post.stats.dislikes}</span>
                <span>💬 {post.stats.comments}</span>
              </div>
            </div>
          ))}
        </div>

        {loading && (
          <div className="hashtag-loading">
            <div className="spinner" />
          </div>
        )}

        {!loading && hasMore && posts.length > 0 && (
          <button className="load-more-btn" onClick={() => fetchPosts(offset)}>
            Load more
          </button>
        )}

        {!loading && !error && posts.length === 0 && (
          <div className="hashtag-empty">
            <p>No posts found for <strong>#{tag}</strong></p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HashtagFeedPage;
